/**
 * VenueDetailPage.tsx
 * Route: /venue/:id
 */
import React, { useEffect, useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { useAppContext } from '../contexts/AppContext'
import { getVenueById } from '../services/venueService'
import { fmtTime, isVenueActiveNow, isVenueOpenForBusiness, getVenueActiveDays, verifiedAgo, getEffectiveVenueStatus, downgradeForClosedDoors } from '../utils/filters'
import { getScheduleStatus, STATUS_VISUALS } from '../utils/happeningNow'
import { DEAL_TYPE_COLORS, DEAL_TYPE_LABELS, CATEGORY_LABELS } from '../types'
import { Analytics } from '../services/analytics'
import { SuggestEditForm } from '../components/ContributionForms'
import { ClaimVenueForm } from '../components/ClaimVenueForm'
import { useConfirmDeal } from '../hooks/useConfirmDeal'
import { PhotoGallery } from '../components/PhotoGallery'
import { track } from '../services/analytics'
import { openExternal } from '../utils/openExternal'
import { fmtDealValue } from '../utils/dealFormat'
import { trackImpression } from '../services/impressionBuffer'
import { getVenueEvents } from '../services/venueEventService'
import type { VenueEvent } from '../services/venueEventService'
import { EventCard } from '../components/EventCard'
import type { Venue, HappyHourSchedule, HappyHourStatus, ScheduleStatus } from '../types'

const STATUS_PRIORITY: HappyHourStatus[] = ['live_now','ends_soon','starts_soon','later_today','ended','not_today']
const JS_DAY_TO_ABBR = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat']
const DEAL_TYPE_ORDER = ['beer', 'cocktail', 'liquor', 'wine', 'food', 'general']

const DAY_TO_RRULE: Record<string, string> = {
  Mon: 'MO', Tue: 'TU', Wed: 'WE', Thu: 'TH',
  Fri: 'FR', Sat: 'SA', Sun: 'SU',
}

function getBestDay(schedules: HappyHourSchedule[]): string {
  if (!schedules.length) return JS_DAY_TO_ABBR[new Date().getDay()]
  const todayAbbr = JS_DAY_TO_ABBR[new Date().getDay()]
  const allDays = Array.from(new Set(schedules.flatMap(s => s.days)))
  if (allDays.includes(todayAbbr as any)) return todayAbbr
  const dayOrder = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun']
  const todayIdx = dayOrder.indexOf(todayAbbr)
  for (let i = 1; i <= 7; i++) {
    const next = dayOrder[(todayIdx + i) % 7]
    if (allDays.includes(next as any)) return next
  }
  return allDays[0] || todayAbbr
}

// Deduplicates schedules with identical time windows, merging their deals.
// Prevents "All day  All day" when a venue has two overlapping all-day schedules.
function getScheduleBlocksForDay(schedules: HappyHourSchedule[], day: string) {
  const matching = schedules.filter(s => s.days.includes(day as any))
  if (!matching.length) return []

  const sorted = [...matching].sort((a, b) => {
    if (a.is_all_day && !b.is_all_day) return -1
    if (!a.is_all_day && b.is_all_day) return 1
    return a.start_time.localeCompare(b.start_time)
  })

  // Merge schedules with the same time window
  const deduped: HappyHourSchedule[] = []
  for (const schedule of sorted) {
    const existing = deduped.find(s =>
      (s.is_all_day && schedule.is_all_day) ||
      (!s.is_all_day && !schedule.is_all_day &&
        s.start_time === schedule.start_time &&
        s.end_time === schedule.end_time)
    )
    if (existing) {
      const existingDescs = new Set(existing.deals.map(d => d.description.toLowerCase()))
      const newDeals = schedule.deals.filter(d => !existingDescs.has(d.description.toLowerCase()))
      existing.deals = [...existing.deals, ...newDeals]
      if ((schedule.deal_text?.length ?? 0) > (existing.deal_text?.length ?? 0)) {
        existing.deal_text = schedule.deal_text
      }
    } else {
      deduped.push({ ...schedule, deals: [...schedule.deals] })
    }
  }

  return deduped.map(schedule => {
    const sortedDeals = [...schedule.deals].sort((a, b) => {
      const aIdx = DEAL_TYPE_ORDER.indexOf(a.type) ?? 99
      const bIdx = DEAL_TYPE_ORDER.indexOf(b.type) ?? 99
      if (aIdx !== bIdx) return aIdx - bIdx
      const aHasPrice = a.price != null
      const bHasPrice = b.price != null
      if (aHasPrice && !bHasPrice) return -1
      if (!aHasPrice && bHasPrice) return 1
      if (aHasPrice && bHasPrice && a.price !== b.price) return (a.price ?? 0) - (b.price ?? 0)
      return a.description.toLowerCase().localeCompare(b.description.toLowerCase())
    })

    const time = schedule.is_all_day
      ? 'All day'
      : `${fmtTime(schedule.start_time)} – ${fmtTime(schedule.end_time)}`

    return { schedule, deals: sortedDeals, time }
  })
}

function HeartIcon({ filled }: { filled: boolean }) {
  return (
    <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
      <path d="M10 17S3 12.3 3 7.5C3 5.3 4.8 3.5 7 3.5c1.4 0 2.6.7 3.3 1.8C11 4.2 12.2 3.5 13.5 3.5c2.2 0 4 1.8 4 4C17.5 12.3 10 17 10 17z"
        fill={filled ? '#E24B4A' : 'none'}
        stroke={filled ? '#E24B4A' : 'currentColor'}
        strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
      />
    </svg>
  )
}

function PhoneIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
      <path d="M14 11.3c0 .3-.1.6-.2.9-.1.3-.3.5-.5.7-.4.4-.8.6-1.3.6-.3 0-.7-.1-1-.2-.3-.1-.6-.3-.9-.5l-.8-.5c-.9-.6-1.7-1.3-2.4-2-.7-.7-1.4-1.5-2-2.4l-.5-.8c-.2-.3-.4-.6-.5-.9-.1-.3-.2-.7-.2-1 0-.5.2-.9.5-1.3.2-.2.4-.4.7-.5.3-.1.6-.2.9-.2.1 0 .2 0 .3.1.1.1.2.2.3.4l1.1 1.5c.1.1.2.3.2.4 0 .1 0 .3-.1.4-.1.1-.1.2-.2.3L7 7.1c.5.9 1 1.5 1.9 2l.5-.5c.1-.1.3-.2.4-.3.1-.1.3-.1.4-.1.1 0 .3.1.4.2l1.5 1.1c.2.1.3.2.4.3 0 .1.1.3 0 .5z"
        stroke="currentColor" strokeWidth="1.2" fill="none"/>
    </svg>
  )
}

export default function VenueDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { favorites, venues: allVenues } = useAppContext()
  const [venue, setVenue] = useState<Venue | null>(null)
  const [loading, setLoading] = useState(true)
  const [showEditForm, setShowEditForm] = useState(false)
  const [showClaimForm, setShowClaimForm] = useState(false)
  const confirmDeal = useConfirmDeal()
  const [activeDay, setActiveDay] = useState('')
  const [venueEvents, setVenueEvents] = useState<VenueEvent[]>([])

  function handleShare() {
    const url = window.location.href
    const text = `Check out happy hour at ${venue?.name} 🍺`
    if (navigator.share) {
      navigator.share({ title: venue?.name ?? 'Happy Hour', text, url })
    } else {
      navigator.clipboard.writeText(url)
      const btn = document.activeElement as HTMLElement
      const orig = btn?.textContent ?? ''
      if (btn) {
        btn.textContent = 'Copied!'
        setTimeout(() => { btn.textContent = orig }, 1500)
      }
    }
    track('venue_shared', { venue_id: venue?.id, venue_name: venue?.name, source: 'detail' })
  }

  // iOS (incl. iPadOS, which masquerades as Mac but has touch)
  function isIOSDevice(): boolean {
    return /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  }

  // First upcoming date (today included) that falls on one of the
  // schedule's days. The old code anchored the event to TODAY regardless,
  // so adding a Monday-only happy hour on a Tuesday produced a weekly
  // event anchored to Tuesday.
  function nextDateForSchedule(days: string[]): Date {
    const d = new Date()
    for (let i = 0; i < 7; i++) {
      if (days.includes(JS_DAY_TO_ABBR[d.getDay()] as any)) return d
      d.setDate(d.getDate() + 1)
    }
    return new Date()
  }

  function addToCalendar(schedule: HappyHourSchedule) {
    const base = nextDateForSchedule(schedule.days)
    const [sh, sm] = schedule.start_time.split(':').map(Number)
    const [eh, em] = schedule.end_time.split(':').map(Number)
    const start = new Date(base.getFullYear(), base.getMonth(), base.getDate(), sh, sm || 0)
    const end   = new Date(base.getFullYear(), base.getMonth(), base.getDate(), eh, em || 0)
    // Past-midnight end (10 PM – 1 AM): end lands on the next day
    if (end <= start) end.setDate(end.getDate() + 1)

    const title = `Happy Hour at ${venue?.name}`
    const details = schedule.deal_text || 'Happy hour deals'
    const location = venue?.address || venue?.neighborhood || ''
    const byday = schedule.days.map(d => DAY_TO_RRULE[d]).filter(Boolean).join(',')

    if (isIOSDevice()) {
      // Serve a real .ics — iOS opens the native event preview, which
      // adds to Apple Calendar. Floating local times, no TZ conversion.
      const fmtLocal = (d: Date) =>
        `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}` +
        `T${String(d.getHours()).padStart(2, '0')}${String(d.getMinutes()).padStart(2, '0')}00`
      const params = new URLSearchParams({
        title, details, location,
        dtstart: fmtLocal(start),
        dtend:   fmtLocal(end),
      })
      if (byday) params.set('byday', byday)
      openExternal(`${window.location.origin}/api/calendar-event?${params.toString()}`)
    } else {
      // Android / desktop: Google Calendar template (UTC-formatted)
      const fmt = (d: Date) => d.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z'
      const rrule = byday ? `RRULE:FREQ=WEEKLY;BYDAY=${byday}` : 'RRULE:FREQ=WEEKLY'
      const url = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(title)}&dates=${fmt(start)}/${fmt(end)}&details=${encodeURIComponent(details)}&location=${encodeURIComponent(location)}&recur=${encodeURIComponent(rrule)}`
      openExternal(url)
    }
    track('add_to_calendar', { venue_id: venue?.id, venue_name: venue?.name, platform: isIOSDevice() ? 'ios' : 'other' })
  }

  function openDirections() {
    if (!venue) return
    const q = venue.address
      ? encodeURIComponent(venue.address)
      : `${encodeURIComponent(venue.name + ' ' + venue.city)}`
    openExternal(`https://www.google.com/maps/dir/?api=1&destination=${q}`)
    Analytics.getDirectionsClicked(venue.id, venue.name)
  }

  function handleBack() {
    if (window.history.state?.idx > 0) {
      navigate(-1)
    } else {
      navigate('/')
    }
  }

  useEffect(() => { window.scrollTo(0, 0) }, [id])

  useEffect(() => {
    if (!id) return
    const cached = allVenues.find(v => v.id === id)
    if (cached) {
      setVenue(cached)
      setLoading(false)
      setActiveDay(getBestDay(cached.schedules ?? []))
    }
    getVenueById(id).then(async v => {
      if (v) {
        setVenue(v)
        setActiveDay(getBestDay(v.schedules ?? []))
        Analytics.venueDetailViewed(v.id, v.name)
        Analytics.pageVisited('venue_detail')
        // detail_view goes through impressionBuffer — batched, and deduped
        // per session so revisits don't inflate Trending. Direct inserts
        // to venue_impressions from components are banned (see buffer docs).
        trackImpression(v.id, 'detail_view')
        confirmDeal.loadCountsForVenues([v.id])
        getVenueEvents(v.id).then(setVenueEvents)
      }
      setLoading(false)
    })
  }, [id]) // eslint-disable-line

  if (loading) return <div className="loading-msg">Loading...</div>
  if (!venue) return (
    <div className="detail-not-found">
      <p>Venue not found.</p>
      <Link to="/">← Back to browse</Link>
    </div>
  )

  const schedules = venue.schedules ?? []
  const isOpen = isVenueActiveNow(venue)
  const isFav = favorites.isFavorite(venue.id)

  const venueStatus: ScheduleStatus | null = (() => {
    const statuses = schedules
      .map(s => getScheduleStatus(s))
      .filter((s): s is ScheduleStatus => s !== null)
    if (!statuses.length) return null
    const best = statuses.sort((a, b) =>
      STATUS_PRIORITY.indexOf(a.status) - STATUS_PRIORITY.indexOf(b.status)
    )[0]
    // Downgrade live_now/ends_soon if venue isn't physically open yet
    if (
      best &&
      !isVenueOpenForBusiness(venue) &&
      (best.status === 'live_now' || best.status === 'ends_soon')
    ) {
      return downgradeForClosedDoors(venue, best)
    }
    return best
  })()

  const vis = venueStatus ? STATUS_VISUALS[venueStatus.status] : null

  const related = allVenues
    .filter(v => v.id !== venue.id && v.neighborhood === venue.neighborhood)
    .slice(0, 3)

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'BarOrCafe',
    name: venue.name,
    address: venue.address ? {
      '@type': 'PostalAddress',
      streetAddress: venue.address,
      addressLocality: venue.city,
    } : undefined,
    url: venue.website ?? undefined,
    telephone: venue.phone ?? undefined,
    geo: venue.latitude && venue.longitude ? {
      '@type': 'GeoCoordinates',
      latitude: venue.latitude,
      longitude: venue.longitude,
    } : undefined,
  }

  return (
    <>
      <Helmet>
        <title>{venue.name} Happy Hour — {venue.neighborhood}, {venue.city}</title>
        <meta name="description" content={`${venue.name} happy hour deals in ${venue.neighborhood}, ${venue.city}. ${schedules[0]?.deal_text ?? 'See current specials and hours.'}`} />
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      </Helmet>

      <div className="detail-page">

        {/* ── NAV BAR ── */}
        <nav className="detail-nav">
          <button className="detail-back-btn" onClick={handleBack}>← Back</button>
          <div className="detail-nav-actions">
            <button className="detail-share-btn" onClick={openDirections}>
              🗺️ Directions
            </button>
            <button className="detail-share-btn" onClick={handleShare}>
              🔗 Share
            </button>
            <button
              className={`detail-fav-btn${isFav ? ' saved' : ''}`}
              onClick={() => favorites.toggleFavorite(venue.id, venue.name)}
            >
              <HeartIcon filled={isFav} />
              {isFav ? 'Saved' : 'Save'}
            </button>
          </div>
        </nav>

        {/* ── HERO CARD ── */}
        <div className="detail-hero-card">
          {venue.image_url && (
            <img src={venue.image_url} alt={venue.name} className="detail-hero-photo" />
          )}

          <div className="detail-hero-body">
            {/* Status + featured badges */}
            <div className="detail-badges">
              {venue.is_featured && (
                <span className="detail-badge detail-badge--featured">⭐ Featured</span>
              )}
              {venueStatus && vis && (
                <span
                  className="detail-badge detail-badge--status"
                  style={{ background: vis.bg, color: vis.text, borderColor: vis.border }}
                >
                  <span
                    className={`status-dot${vis.pulse ? ' pulse' : ''}`}
                    style={{ background: vis.dot }}
                  />
                  {venueStatus.badge}
                </span>
              )}
            </div>

            {/* Name + meta */}
            <h1 className="detail-name">{venue.name}</h1>
            <div className="detail-meta">
              <span className="detail-meta-location">{venue.neighborhood}, {venue.city}</span>
              {venue.price_tier && (
                <span className="detail-meta-dot">·</span>
              )}
              {venue.price_tier && (
                <span className="detail-price">{venue.price_tier}</span>
              )}
              {(venue.verification_status === 'verified' || venue.verification_status === 'claimed') && (
                <span className="detail-verified-badge">✓ Verified</span>
              )}
            </div>

            {/* Category tags */}
            {venue.categories.length > 0 && (
              <div className="detail-tags">
                {venue.categories.map(cat => (
                  <span key={cat} className="detail-tag">{CATEGORY_LABELS[cat]}</span>
                ))}
                {venue.dog_friendly && !venue.categories.includes('dog_friendly' as any) && (
                  <span className="detail-tag">🐾 Dog Friendly</span>
                )}
              </div>
            )}
          </div>
        </div>

        {/* ── INFO ── */}
        {(venue.address || venue.website || venue.phone) && (
          <div className="detail-card">
            <h2 className="detail-section-title">Info</h2>
            <div className="detail-info-grid">
              {venue.address && (
                <div className="detail-info-row">
                  <span className="detail-info-label">ADDRESS</span>
                  <a
                    href="#"
                    className="detail-info-link"
                    onClick={e => {
                      e.preventDefault()
                      openExternal(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(venue.address!)}`)
                      Analytics.getDirectionsClicked(venue.id, venue.name)
                    }}
                  >
                    {venue.address} ↗
                  </a>
                </div>
              )}
              {venue.website && (
                <div className="detail-info-row">
                  <span className="detail-info-label">WEBSITE</span>
                  <a
                    href="#"
                    className="detail-info-link"
                    onClick={e => {
                      e.preventDefault()
                      openExternal(venue.website!)
                      Analytics.outboundWebsiteClicked(venue.id, venue.website!)
                    }}
                  >
                    Visit website ↗
                  </a>
                </div>
              )}
              {venue.phone && (
                <div className="detail-info-row">
                  <span className="detail-info-label">PHONE</span>
                  <a
                    href={`tel:${venue.phone}`}
                    className="detail-info-link detail-info-link--phone"
                    onClick={() => {
                      Analytics.phoneClicked(venue.id, venue.name)
                    }}
                  >
                    <PhoneIcon /> {venue.phone}
                  </a>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── MAP ── */}
        {venue.latitude && venue.longitude && process.env.REACT_APP_GOOGLE_PLACES_KEY && (
          <div className="detail-map-wrap">
            <iframe
              title={`Map of ${venue.name}`}
              className="detail-map"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              src={`https://www.google.com/maps/embed/v1/place?key=${process.env.REACT_APP_GOOGLE_PLACES_KEY}&q=${encodeURIComponent(venue.address || venue.name + ' Cincinnati')}&zoom=15`}
            />
            <button className="detail-map-directions" onClick={openDirections}>
              🗺️ Get directions →
            </button>
          </div>
        )}

        {/* ── HAPPY HOUR DEALS ── */}
        <div className="detail-card">
          <h2 className="detail-section-title">Happy Hour Deals</h2>
          {(() => {
            const dayOrder = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun']
            const venueDays = dayOrder.filter(d => schedules.some(s => s.days.includes(d as any)))
            const todayAbbr = JS_DAY_TO_ABBR[new Date().getDay()]
            const selectedDay = activeDay || getBestDay(schedules)
            const isToday = selectedDay === todayAbbr
            const blocks = getScheduleBlocksForDay(schedules, selectedDay)

            return (
              <>
                {/* Day tabs */}
                {venueDays.length > 1 && (
                  <div className="detail-schedule-tabs">
                    {venueDays.map(d => {
                      const dayBlocks = getScheduleBlocksForDay(schedules, d)
                      const hasLiveBlock = d === todayAbbr && dayBlocks.some(b => {
                        const st = getScheduleStatus(b.schedule)
                        return st && (st.status === 'live_now' || st.status === 'ends_soon')
                      })
                      return (
                        <button
                          key={d}
                          className={`detail-tab${selectedDay === d ? ' active' : ''}`}
                          onClick={() => setActiveDay(d)}
                        >
                          {d}
                          {hasLiveBlock && <span className="tab-live-dot" />}
                        </button>
                      )
                    })}
                  </div>
                )}

                {blocks.length === 0 ? (
                  <p className="detail-deal-text">No deals scheduled for this day.</p>
                ) : (
                  <div className="detail-schedule">
                    {blocks.map((block, blockIdx) => {
                      const st = getScheduleStatus(block.schedule)
                      const blockVis = st ? STATUS_VISUALS[st.status] : null
                      const isLive = st && (st.status === 'live_now' || st.status === 'ends_soon') && isVenueOpenForBusiness(venue)
                      const showStatus = isToday && st

                      return (
                        <div
                          key={block.schedule.id}
                          className="detail-schedule-block"
                          style={blockIdx < blocks.length - 1 ? { borderBottom: '1px dashed #EAE6DF', paddingBottom: 20, marginBottom: 20 } : {}}
                        >
                          {/* Time + status */}
                          <div className="detail-schedule-header">
                            <span
                              className="detail-time"
                              style={isLive ? { color: '#22C55E', fontWeight: 700 } : {}}
                            >
                              {block.time}
                            </span>
                            {showStatus && blockVis && (
                              <span
                                className="detail-schedule-status"
                                style={{ background: blockVis.bg, color: blockVis.text }}
                              >
                                {st.label}
                              </span>
                            )}
                          </div>

                          {/* Add to calendar (not for all-day) */}
                          {!block.schedule.is_all_day && (
                            <button
                              className="detail-cal-btn"
                              onClick={() => addToCalendar(block.schedule)}
                            >
                              📅 Add to Calendar
                            </button>
                          )}

                          {/* Deals */}
                          {block.deals.length > 0 ? (
                            <div className="detail-deals">
                              {block.deals.map((deal, i) => {
                                const valueLabel = fmtDealValue(deal)
                                return (
                                  <div
                                    key={i}
                                    className="detail-deal-row"
                                    style={{
                                      background: DEAL_TYPE_COLORS[deal.type].bg,
                                      color: DEAL_TYPE_COLORS[deal.type].text,
                                    }}
                                  >
                                    <span className="detail-deal-type">{DEAL_TYPE_LABELS[deal.type]}</span>
                                    <span className="detail-deal-desc">{deal.description}</span>
                                    {valueLabel && (
                                      <span className="detail-deal-price">{valueLabel}</span>
                                    )}
                                  </div>
                                )
                              })}
                            </div>
                          ) : block.schedule.deal_text ? (
                            <p className="detail-deal-text">{block.schedule.deal_text}</p>
                          ) : null}
                        </div>
                      )
                    })}
                  </div>
                )}
              </>
            )
          })()}
        </div>

        {/* ── PHOTOS ── */}
        <div className="detail-card">
          <PhotoGallery venueId={venue.id} venueName={venue.name} />
        </div>

        {/* ── TRUST / VERIFICATION ── */}
        <div className="detail-card detail-card--trust">
          <div className="detail-trust-row">
            {/* Pill reflects one honest fact: has this venue's deal info
                ever actually been checked? last_verified_at is stamped
                exactly once, at admin approval — it's a direct record of
                "someone confirmed this was accurate before it went live,"
                not a claim about who submitted the venue. No timestamp,
                no pill — nothing asserted either way. */}
            {venue.last_verified_at && (
              <span className="detail-verification detail-verification--verified">✓ Verified</span>
            )}
            {venue.last_verified_at && (
              <span className="detail-verified-ago">{verifiedAgo(venue)}</span>
            )}
            <button
              className={`detail-confirm-btn${confirmDeal.hasConfirmed(venue.id) ? ' confirmed' : ''}`}
              onClick={() => confirmDeal.confirmDeal(venue.id)}
              disabled={confirmDeal.hasConfirmed(venue.id) || confirmDeal.confirming === venue.id}
            >
              {confirmDeal.hasConfirmed(venue.id)
                ? '✓ You confirmed this'
                : confirmDeal.confirming === venue.id
                  ? 'Saving...'
                  : `👍 Still accurate${(confirmDeal.confirmCounts[venue.id] ?? 0) > 0
                      ? ` (${confirmDeal.confirmCounts[venue.id]} this week)` : ''}`
              }
            </button>
            <button className="detail-suggest-btn" onClick={() => setShowEditForm(true)}>
              Suggest correction
            </button>
          </div>
        </div>

        {showEditForm && (
          <div className="detail-card">
            <SuggestEditForm venue={venue} onClose={() => setShowEditForm(false)} />
          </div>
        )}

        {/* ── CLAIM ── */}
        {!showClaimForm ? (
          <div className="detail-claim-banner">
            <span className="detail-claim-text">Is this your bar?</span>
            <button className="detail-claim-btn" onClick={() => setShowClaimForm(true)}>
              Claim it free →
            </button>
          </div>
        ) : (
          <div className="detail-card">
            <ClaimVenueForm venue={venue} onClose={() => setShowClaimForm(false)} />
          </div>
        )}

        {/* ── UPCOMING EVENTS ── */}
        {venueEvents.length > 0 && (
          <div className="detail-card">
            <h2 className="detail-section-title">Upcoming Events</h2>
            <div className="detail-events-list">
              {venueEvents.map(event => (
                <EventCard key={event.id} event={event} compact={false} />
              ))}
            </div>
          </div>
        )}

        {/* ── ADD TO CRAWL ── */}
        <div className="detail-crawl-banner">
          <div>
            <div className="detail-crawl-title">🍺 Like this spot?</div>
            <div className="detail-crawl-sub">Add it to a bar crawl and find the best happy hour route</div>
          </div>
          <Link
            to={`/crawl?neighborhood=${encodeURIComponent(venue.neighborhood)}`}
            className="detail-crawl-btn"
          >
            Build a crawl →
          </Link>
        </div>

        {/* ── RELATED ── */}
        {related.length > 0 && (
          <div className="detail-card">
            <h2 className="detail-section-title">More in {venue.neighborhood}</h2>
            <div className="detail-related">
              {related.map(v => (
                <Link key={v.id} to={`/venue/${v.id}`} className="detail-related-card">
                  <div className="detail-related-name">{v.name}</div>
                  <div className="detail-related-meta">
                    {v.neighborhood} · {v.price_tier ?? '—'}
                  </div>
                  {isVenueActiveNow(v) && (
                    <span className="detail-related-live">Open now</span>
                  )}
                </Link>
              ))}
            </div>
          </div>
        )}

      </div>
    </>
  )
}
